import { getAppState } from '../app';
import { applyI18n, t } from '../i18n';
import { bindLocaleRefresh } from '../i18n/bindLocaleRefresh';
import { iconImg, type IconName } from '../ui/icons';
import { createHelpTip } from '../help/helpTips';
import {
  loadHomeDashboardData,
  type HomeActionItem,
  type HomeRecentItem,
} from './homeData';

function displayTitle(title: string, fallbackKey: string): string {
  const trimmed = title.trim();
  return trimmed || t(fallbackKey);
}

function createSection(titleKey: string): { section: HTMLElement; body: HTMLElement } {
  const section = document.createElement('section');
  section.className = 'home-section panel';

  const heading = document.createElement('h2');
  heading.className = 'home-section__title';
  heading.setAttribute('data-i18n', titleKey);

  const body = document.createElement('div');
  body.className = 'home-section__body';

  section.append(heading, body);
  return { section, body };
}

function createActionRow(item: HomeActionItem): HTMLAnchorElement {
  const row = document.createElement('a');
  row.className = 'home-row';
  row.href = item.href;

  const main = document.createElement('span');
  main.className = 'home-row__main';

  const title = document.createElement('span');
  title.className = 'home-row__title';
  title.textContent = displayTitle(
    item.title,
    item.kind.startsWith('folder')
      ? 'files.owner.untitledFolder'
      : item.kind.startsWith('room')
        ? 'home.untitled.room'
        : 'info.owner.untitledShare',
  );

  const reason = document.createElement('span');
  reason.className = 'home-row__reason muted';
  reason.textContent = t(item.reasonKey, item.reasonParams);

  main.append(title, reason);
  row.append(iconImg(item.icon, 18), main);
  return row;
}

function createRecentRow(item: HomeRecentItem): HTMLAnchorElement {
  const row = document.createElement('a');
  row.className = 'home-row';
  row.href = item.href;

  const main = document.createElement('span');
  main.className = 'home-row__main';

  const title = document.createElement('span');
  title.className = 'home-row__title';
  title.textContent = displayTitle(
    item.title,
    item.kind === 'folder' ? 'files.owner.untitledFolder' : 'home.untitled.room',
  );

  const meta = document.createElement('span');
  meta.className = 'home-row__reason muted';
  meta.setAttribute('data-i18n', item.kind === 'folder' ? 'home.recent.folder' : 'home.recent.room');

  main.append(title, meta);
  row.append(iconImg(item.icon, 18), main);
  return row;
}

function createModuleTile(options: {
  icon: IconName;
  titleKey: string;
  blurbKey: string;
  primaryHref: string;
  primaryKey: string;
  secondaryHref: string;
  secondaryKey: string;
}): HTMLElement {
  const card = document.createElement('article');
  card.className = 'home-tile card';

  const heading = document.createElement('h3');
  heading.className = 'home-tile__title';
  const title = document.createElement('span');
  title.setAttribute('data-i18n', options.titleKey);
  heading.append(iconImg(options.icon, 20), title);

  const blurb = document.createElement('p');
  blurb.className = 'muted home-tile__blurb';
  blurb.setAttribute('data-i18n', options.blurbKey);

  const actions = document.createElement('div');
  actions.className = 'home-tile__actions';

  const primary = document.createElement('a');
  primary.href = options.primaryHref;
  primary.className = 'button primary';
  primary.setAttribute('data-i18n', options.primaryKey);

  const secondary = document.createElement('a');
  secondary.href = options.secondaryHref;
  secondary.className = 'button ghost';
  secondary.setAttribute('data-i18n', options.secondaryKey);

  actions.append(primary, secondary);
  card.append(heading, blurb, actions);
  return card;
}

export function HomePage(): HTMLElement {
  const page = document.createElement('section');
  page.className = 'home-page';

  const state = getAppState();
  const session = state.kind === 'authenticated' ? state.session : null;
  const userId = session?.userId ?? '';

  const activity = document.createElement('div');
  activity.className = 'home-activity';
  activity.hidden = true;

  const outstandingWrap = createSection('home.outstanding.title');
  outstandingWrap.section.hidden = true;
  const outstandingList = document.createElement('div');
  outstandingList.className = 'home-list';
  outstandingWrap.body.append(outstandingList);

  const recentWrap = createSection('home.recent.title');
  recentWrap.section.hidden = true;
  const recentList = document.createElement('div');
  recentList.className = 'home-list';
  recentWrap.body.append(recentList);

  activity.append(outstandingWrap.section, recentWrap.section);

  const tilesSection = document.createElement('section');
  tilesSection.className = 'home-section';
  const tilesHeading = document.createElement('h2');
  tilesHeading.className = 'home-section__title';
  const tilesTitle = document.createElement('span');
  tilesTitle.setAttribute('data-i18n', 'home.modules.title');
  tilesHeading.append(tilesTitle, createHelpTip('getting-started'));
  const tiles = document.createElement('div');
  tiles.className = 'home-tiles';

  tiles.append(
    createModuleTile({
      icon: 'info-share',
      titleKey: 'nav.information',
      blurbKey: 'home.tile.info.blurb',
      primaryHref: '/information?create=1',
      primaryKey: 'home.tile.info.create',
      secondaryHref: '/information',
      secondaryKey: 'home.tile.info.browse',
    }),
    createModuleTile({
      icon: 'files',
      titleKey: 'nav.files',
      blurbKey: 'home.tile.files.blurb',
      primaryHref: '/files?create=1',
      primaryKey: 'home.tile.files.create',
      secondaryHref: '/files',
      secondaryKey: 'home.tile.files.browse',
    }),
    createModuleTile({
      icon: 'sedr',
      titleKey: 'nav.sedr',
      blurbKey: 'home.tile.sedr.blurb',
      primaryHref: '/sedr?create=1',
      primaryKey: 'home.tile.sedr.create',
      secondaryHref: '/sedr',
      secondaryKey: 'home.tile.sedr.browse',
    }),
  );

  tilesSection.append(tilesHeading, tiles);
  page.append(tilesSection, activity);
  applyI18n(page);

  bindLocaleRefresh(() => applyI18n(page));

  if (userId) {
    void loadHomeDashboardData(userId).then((data) => {
      if (!page.isConnected) return;

      outstandingList.replaceChildren(...data.outstanding.map(createActionRow));
      outstandingWrap.section.hidden = data.outstanding.length === 0;

      recentList.replaceChildren(...data.recent.map(createRecentRow));
      recentWrap.section.hidden = data.recent.length === 0;

      activity.hidden = data.outstanding.length === 0 && data.recent.length === 0;

      applyI18n(page);
    });
  }

  return page;
}
